/**
 * XSite Navigation Manager für Browser-History und URL-Management
 * Behandelt Navigation zu /de/web/ URLs mit korrekter Browser-History
 */

export class XSiteNavigationManager {
  constructor() {
    this.currentPath = '';
    this.isNavigating = false;
    this.router = null;
    this.setupPopstateHandler();
    this.setupLinkInterception();
  }

  /**
   * Set Next.js router instance
   */
  setRouter(router) {
    this.router = router;
    //console.log('[XSite Navigation] ✅ Next.js router set');
  }

  /**
   * Browser-Back-Button Handler
   */
  setupPopstateHandler() {
    window.addEventListener('popstate', (event) => {
      //console.log('[XSite Navigation] 🔄 Browser back/forward detected:', event.state);
      
      if (event.state && event.state.xsitePath) {
        // Browser-Titel wiederherstellen
        if (event.state.pageTitle) {
          document.title = `${event.state.pageTitle} | XOFFI`;
          //console.log('[XSite Navigation] 📄 Browser title restored to:', document.title);
        }
        
        // Verwende Next.js Router für Navigation
        if (this.router) {
          this.router.push(event.state.xsitePath);
        }
      }
    });
  }

  /**
   * Link-Interception für /de/web/ URLs
   */
  setupLinkInterception() {
    document.addEventListener('click', (event) => {
      const link = event.target.closest('a');
      if (!link) return;

      const href = link.getAttribute('href');
      // 🚨 TEMPORÄR DEAKTIVIERT: Lasse /de/web/ Links zu page.jsx durchgehen
      if (!href || !href.startsWith('/de/web/')) return;
      
      //console.log('[XSite Navigation] 🚨 DEACTIVATED: Would have intercepted link:', href);
      //console.log('[XSite Navigation] 🎯 Letting /de/web/ links go to page.jsx instead');
      return; // Exit early - keine Interception!

      // Verhindere normale Navigation
      event.preventDefault();
      //console.log('[XSite Navigation] 🔗 Intercepted link click:', href);

      // Lade via Next.js Router mit History-Update
      this.navigateToXSite(href);
    });
  }

  /**
   * Navigiere zu XSite URL mit Browser-History-Update
   */
  async navigateToXSite(deWebUrl, pageTitle = null) {
    if (this.isNavigating) return;
    
    //console.log('[XSite Navigation] 🚀 Navigating to:', deWebUrl);
    //console.log('[XSite Navigation] 📄 Page title:', pageTitle);
    this.isNavigating = true;

    try {
      const titleForHistory = pageTitle || 'XOFFI';
      
      // Browser-Titel sofort setzen
      if (pageTitle) {
        document.title = `${pageTitle} | XOFFI`;
        //console.log('[XSite Navigation] 📄 Browser title set to:', document.title);
      }
      
      // Konvertiere /de/web/ zu /xsite/10/ für echte Navigation
      const xsitePath = deWebUrl.replace('/de/web/', '/xsite/10/');
      //console.log('[XSite Navigation] 🔄 Converting URL for navigation:', deWebUrl, '→', xsitePath);
      
      // Verwende Next.js Router für echte Navigation zu XSite
      if (this.router) {
        //console.log('[XSite Navigation] 🔄 Using Next.js router.push() to:', xsitePath);
        //console.log('[XSite Navigation] 📍 Current pathname:', window.location.pathname);
        
        // Prüfe ob wir bereits auf der gleichen Route sind
        const currentPath = window.location.pathname;
        if (currentPath === xsitePath) {
          //console.log('[XSite Navigation] ⚠️ Already on target route, forcing reload...');
          // Wenn wir bereits auf der Route sind, force reload
          window.location.href = xsitePath;
          return;
        }
        
        try {
          await this.router.replace(xsitePath);
          //console.log('[XSite Navigation] ✅ router.replace() completed');
        } catch (error) {
          console.error('[XSite Navigation] ❌ router.replace() error:', error);
          // Fallback bei Fehler
          window.location.href = xsitePath;
          return;
        }
        
        // Nach der Navigation: Browser-URL zu de/web/ ändern (ohne Reload)
        //console.log('[XSite Navigation] 🔄 Updating browser URL to de/web format');
        window.history.replaceState(
          { 
            xsitePath: deWebUrl,
            pageTitle: titleForHistory
          },
          titleForHistory,
          deWebUrl  // Browser zeigt /de/web/... an
        );
        
        //console.log('[XSite Navigation] ✅ Navigation complete, browser URL set to:', deWebUrl);
      } else {
        console.warn('[XSite Navigation] ⚠️ No Next.js router available, using fallback');
        // Fallback: Vollständige Navigation
        window.location.href = xsitePath;
      }
      
    } catch (error) {
      console.error('[XSite Navigation] ❌ Navigation error:', error);
    } finally {
      this.isNavigating = false;
    }
  }

  /**
   * Legacy: Lade XSite Content (für Rückwärtskompatibilität)
   */
  async loadXSiteContent(deWebUrl, updateHistory = true) {
    //console.log('[XSite Navigation] 📥 Legacy loadXSiteContent called, redirecting to router navigation');
    // updateHistory parameter is kept for API compatibility but not used
    void updateHistory; // ESLint: Acknowledge unused parameter
    
    // Leite zu Router-basierter Navigation um
    if (this.router) {
      await this.router.push(deWebUrl);
    } else {
      window.location.href = deWebUrl;
    }
  }

  /**
   * Auth-Token holen - konsistent mit auth-helper.js
   */
  getAuthToken() {
    // Use the same logic as auth-helper.js for consistency
    const authToken = localStorage.getItem('auth_token') || 
                     localStorage.getItem('token') ||
                     localStorage.getItem('auth-token'); // Legacy fallback
    
    if (authToken) {
      return authToken;
    }
    
    // Fallback: Try cookies
    try {
      const cookies = document.cookie.split(';');
      for (let cookie of cookies) {
        const [name, value] = cookie.trim().split('=');
        if (name === 'auth_token' || name === 'token' || name === 'auth-token') {
          return decodeURIComponent(value);
        }
      }
    } catch (error) {
      console.warn('[XSite Navigation] Error reading cookies:', error);
    }
    
    return null;
  }
}

// Global instance
let navigationManager = null;

/**
 * Initialisiere XSite Navigation Manager
 */
export function initializeXSiteNavigation(router = null) {
  if (!navigationManager) {
    navigationManager = new XSiteNavigationManager();
    //console.log('[XSite Navigation] ✅ Navigation manager initialized');
  }
  
  if (router && navigationManager) {
    navigationManager.setRouter(router);
  }
  
  return navigationManager;
}

/**
 * Get current navigation manager instance
 */
export function getXSiteNavigationManager() {
  return navigationManager;
} 